export type Camera = { x: number; y: number; z: number }; // screen = (world - cam) * z
export type Point = { x: number; y: number };
export type Rect = { minX: number; minY: number; maxX: number; maxY: number };

export const MIN_Z = 0.02;
export const MAX_Z = 8;

export const worldToScreen = (c: Camera, p: Point): Point => ({ x: (p.x - c.x) * c.z, y: (p.y - c.y) * c.z });
export const screenToWorld = (c: Camera, p: Point): Point => ({ x: p.x / c.z + c.x, y: p.y / c.z + c.y });

/** Zoom by `factor` keeping the world point under `pointer` (screen px) fixed. */
export function zoomAt(c: Camera, pointer: Point, factor: number): Camera {
  const z = Math.min(MAX_Z, Math.max(MIN_Z, c.z * factor));
  const world = screenToWorld(c, pointer);
  return { z, x: world.x - pointer.x / z, y: world.y - pointer.y / z };
}

/** World rect covered by the viewport, grown by `margin` (fraction of its size) on each side. */
export function visibleRect(c: Camera, vw: number, vh: number, margin = 0.5): Rect {
  const w = vw / c.z;
  const h = vh / c.z;
  return { minX: c.x - w * margin, minY: c.y - h * margin, maxX: c.x + w * (1 + margin), maxY: c.y + h * (1 + margin) };
}

/** Screen margins taken by floating bars over the canvas, from each side of the viewport. */
export type Insets = { top: number; right: number; bottom: number; left: number };
export const NO_INSETS: Insets = { top: 0, right: 0, bottom: 0, left: 0 };

/**
 * The margins that bars take, from their rectangles relative to the viewport: a wide bar takes from the top or the
 * bottom (the half its centre is in), a tall one from the left or the right; an empty rectangle (hidden) takes nothing.
 */
export function insetsOf(bars: readonly { left: number; top: number; right: number; bottom: number }[], vw: number, vh: number): Insets {
  const out = { ...NO_INSETS };
  for (const b of bars) {
    const w = b.right - b.left;
    const h = b.bottom - b.top;
    if (w <= 0 || h <= 0) continue;
    if (w >= h) {
      if ((b.top + b.bottom) / 2 < vh / 2) out.top = Math.max(out.top, b.bottom);
      else out.bottom = Math.max(out.bottom, vh - b.top);
    } else if ((b.left + b.right) / 2 < vw / 2) out.left = Math.max(out.left, b.right);
    else out.right = Math.max(out.right, vw - b.left);
  }
  return out;
}

type Box = { left: number; top: number; right: number; bottom: number };

/**
 * `fitRect` that also keeps `r` off panels sitting in a corner (the overview): each is taken from its horizontal or
 * its vertical side, whichever leaves the larger zoom; an empty box (hidden) is ignored.
 */
export function fitClear(r: Rect, vw: number, vh: number, pad: number, inset: Insets, corners: readonly Box[]): Camera {
  const boxes = corners.filter((b) => b.right > b.left && b.bottom > b.top);
  let best = fitRect(r, vw, vh, pad, inset);
  if (!boxes.length) return best;
  best = { ...best, z: -Infinity };
  for (let mask = 0; mask < 1 << boxes.length; mask++) {
    const i = { ...inset };
    boxes.forEach((b, k) => {
      if (mask & (1 << k)) {
        if ((b.left + b.right) / 2 < vw / 2) i.left = Math.max(i.left, b.right);
        else i.right = Math.max(i.right, vw - b.left);
      } else if ((b.top + b.bottom) / 2 < vh / 2) i.top = Math.max(i.top, b.bottom);
      else i.bottom = Math.max(i.bottom, vh - b.top);
    });
    const c = fitRect(r, vw, vh, pad, i);
    if (c.z > best.z) best = c;
  }
  return best;
}

/** The camera that shows `r` whole, `pad` px clear of the viewport edges and of the bars' `inset`, centred in the rest. */
export function fitRect(r: Rect, vw: number, vh: number, pad = 48, inset: Insets = NO_INSETS): Camera {
  const w = r.maxX - r.minX;
  const h = r.maxY - r.minY;
  const aw = vw - inset.left - inset.right;
  const ah = vh - inset.top - inset.bottom;
  const z = Math.min(MAX_Z, Math.max(MIN_Z, Math.min((aw - pad * 2) / w, (ah - pad * 2) / h)));
  return { z, x: r.minX - inset.left / z - (aw / z - w) / 2, y: r.minY - inset.top / z - (ah / z - h) / 2 };
}

/** Pan by a screen-space delta: dragging content right means the camera moves left. */
export const panBy = (c: Camera, dxScreen: number, dyScreen: number): Camera => ({
  ...c,
  x: c.x - dxScreen / c.z,
  y: c.y - dyScreen / c.z,
});
